import { trpcClient } from '@client/trpc/client';
import { HANDLING_PARTY_FILTER_SENTINELS, SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { match } from 'ts-pattern';
import { createUserFilterValue, UserFilterValue } from '../components/UserFilterValue';
import { buildUserWhere } from './auto-where-builders';
import { isRelativeDateToken, relativeDateLabel, resolveRelativeDateRange } from './relative-date';
import type {
    FieldConfig,
    FieldType,
    FilterConfig,
    FilterOperator,
    InferCustomWhereBuilder,
    MultiselectFieldConfig,
    OperatorsByFieldType,
    PrismaWhereClause,
    TextSearchFieldConfig,
    TypedCustomWhereBuilder,
    TypedFieldConfig,
} from './types';

/** Safely convert a value to BigInt, returning null if conversion fails */
const safeBigInt = (v: unknown): bigint | null => {
    if (v === null || v === undefined || v === '') return null;
    try {
        return BigInt(v as string);
    } catch {
        return null;
    }
};

/** Coerce a date filter value to a `Date`, or `null` for empty/invalid input (skips the filter). */
const toDateOrNull = (value: unknown): Date | null => {
    if (value === null || value === undefined || value === '') return null;
    const d = value instanceof Date ? value : new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** Format a stored date filter value for chip display: relative token → label, else ISO date. */
const formatDateValue = (value: unknown, dateOnly: boolean): string => {
    if (isRelativeDateToken(value)) return relativeDateLabel(value) ?? String(value);
    const d = value instanceof Date ? value : new Date(value as string | number);
    if (Number.isNaN(d.getTime())) return String(value ?? '');
    return dateOnly ? d.toISOString().split('T')[0] : d.toISOString();
};

/**
 * Default options loader for user fields.
 * Fetches users from the API and formats them for filter options.
 */
const defaultUserOptionsLoader = async () => {
    const { rows } = await trpcClient.user.listUsersForSelector.query({});
    return rows.map((user) => ({
        label: user.fullName || user.email,
        value: user.userId,
    }));
};

type UserOption = { label: string; value: bigint; subtitle?: string };
type UserOptionsSource<TWhere = any> =
    | Array<UserOption>
    | (() => Promise<Array<UserOption>>)
    | ((whereClause?: TWhere) => Promise<Array<UserOption>>);

const UNALLOCATED_USER_OPTION: UserOption = {
    label: 'Unallocated',
    value: SYSTEM_ACCOUNT_IDS.UNALLOCATED,
};

/** Synthetic "Me" option — resolves to the viewer's own id at where-build time. */
const CURRENT_USER_OPTION: UserOption = {
    label: 'Me',
    value: SYSTEM_ACCOUNT_IDS.CURRENT_USER,
};

/**
 * Wraps a user/handling-party options source so the resolved options include a synthetic
 * sentinel option prepended (e.g. "Unallocated", "Me", "Mine"). Required for the FilterChip
 * label lookup so a stored sentinel value resolves to its label rather than its raw numeric form.
 */
const withPrependedOption = <TWhere>(
    option: UserOption,
    source: UserOptionsSource<TWhere> | undefined,
): ((whereClause?: TWhere) => Promise<Array<UserOption>>) => {
    const base = source ?? defaultUserOptionsLoader;
    return async (whereClause?: TWhere) => {
        const resolved = Array.isArray(base)
            ? base
            : base.length > 0
              ? await (base as (w?: TWhere) => Promise<Array<UserOption>>)(whereClause)
              : await (base as () => Promise<Array<UserOption>>)();
        return [option, ...resolved];
    };
};

/**
 * Build the user-field options loader, layering the synthetic sentinels onto the
 * base source. "Current user" is included whenever a `currentUserId` is available
 * (so stored sentinels always resolve to a label), independent of whether the
 * picker currently offers it.
 */
function buildUserOptionsLoader<TWhere>(
    source: UserOptionsSource<TWhere> | undefined,
    opts: { showUnallocated?: boolean; includeCurrentUser?: boolean },
): UserOptionsSource<TWhere> {
    let loader: UserOptionsSource<TWhere> = source ?? defaultUserOptionsLoader;
    if (opts.showUnallocated) loader = withPrependedOption<TWhere>(UNALLOCATED_USER_OPTION, loader);
    if (opts.includeCurrentUser) loader = withPrependedOption<TWhere>(CURRENT_USER_OPTION, loader);
    return loader;
}

/**
 * Resolve the "Current user" sentinel in a filter value to the viewer's id. Unresolved
 * sentinels (no current user) are dropped so the clause never queries the sentinel itself.
 * Runs only in the where-build path (via the field `transform`), so the stored value keeps
 * the sentinel and a shared view stays identical for every viewer.
 */
function resolveCurrentUserValue(value: unknown, currentUserId: bigint | undefined): bigint | bigint[] | null {
    const resolveOne = (v: unknown): bigint | null => {
        const b = safeBigInt(v);
        if (b === SYSTEM_ACCOUNT_IDS.CURRENT_USER) return currentUserId ?? null;
        return b;
    };
    if (Array.isArray(value)) {
        return value.map(resolveOne).filter((v): v is bigint => v !== null);
    }
    return resolveOne(value);
}

/**
 * Build the synthetic "Mine" handling-party option. It expands to the viewer's handling-party ids
 * at where-build time; the grouped picker card is subtitled with how many parties that is (e.g.
 * "Your 3 handling parties") rather than listing names, which can be long and numerous.
 */
function buildMineHandlingPartyOption(currentUserHandlingPartyIds: readonly bigint[] | undefined): UserOption {
    const count = currentUserHandlingPartyIds?.length ?? 0;
    const subtitle = count > 0 ? `Your ${count} handling ${count === 1 ? 'party' : 'parties'}` : undefined;
    return { label: 'Mine', value: HANDLING_PARTY_FILTER_SENTINELS.CURRENT_USER, subtitle };
}

/**
 * Expand the "Mine" handling-party sentinel in a filter value into the viewer's handling-party
 * ids (one → many, deduped). Other ids pass through; nulls and an unresolved sentinel (no
 * handling parties) drop out. Runs only in the where-build path (via the field `transform`), so
 * the stored value keeps the sentinel and a shared view stays identical for every viewer.
 */
function resolveMineHandlingParties(value: unknown, currentUserHandlingPartyIds: readonly bigint[]): bigint[] {
    const arr = Array.isArray(value) ? value : value != null ? [value] : [];
    const out: bigint[] = [];
    for (const v of arr) {
        const b = safeBigInt(v);
        if (b === null) continue;
        if (b === HANDLING_PARTY_FILTER_SENTINELS.CURRENT_USER) {
            out.push(...currentUserHandlingPartyIds);
        } else {
            out.push(b);
        }
    }
    return [...new Set(out)];
}

/**
 * Field Configuration Utilities
 *
 * This module provides utilities for creating type-safe filter configurations,
 * field definitions, and custom where clause builders for the filter system.
 *
 * @module field-config
 */

/**
 * Validates that field configurations have either dbField or customWhereBuilder
 * Use this in development to catch configuration errors early
 */
export function validateFieldConfig<TWhere>(fieldConfig: FieldConfig<any, TWhere>): void {
    if (process.env.NODE_ENV !== 'production') {
        if (!('dbField' in fieldConfig) && !fieldConfig.customWhereBuilder) {
            console.error(
                `Filter configuration "${fieldConfig.id}" must have either dbField or customWhereBuilder to generate where clauses`,
            );
        }
    }
}

/**
 * Create a filter configuration with type inference
 *
 * Main entry point for defining filters for an entity. Combines multiple field
 * configurations into a complete filter configuration.
 *
 * @param filters - Array of field configurations
 * @param options - Optional configuration options
 * @param options.defaultOperators - Override default operators for field types
 * @returns Complete filter configuration
 *
 * @example
 * ```typescript
 * const userFilters = createFilterConfig<UserWhere>([
 *     commonFields.text({ id: 'name', label: 'Name', dbField: 'name' }),
 *     commonFields.select({
 *         id: 'status',
 *         label: 'Status',
 *         options: [{ label: 'Active', value: 'active' }],
 *         dbField: 'status'
 *     }),
 *     commonFields.date({ id: 'created', label: 'Created At', dbField: 'createdAt' })
 * ]);
 * ```
 */
export function createFilterConfig<TWhere>(
    filters: (FieldConfig<any, TWhere> | TypedFieldConfig<any, TWhere, any> | TextSearchFieldConfig<TWhere>)[],
    options?: { defaultOperators?: Partial<Record<FieldType, FilterOperator[]>> },
): FilterConfig<TWhere> {
    // Validate all field configurations in development
    if (process.env.NODE_ENV !== 'production') {
        filters.forEach((filter) => validateFieldConfig(filter as FieldConfig<any, TWhere>));
    }

    return {
        filters: filters as (FieldConfig<any, TWhere> | TextSearchFieldConfig<TWhere>)[],
        defaultOperators: options?.defaultOperators,
    };
}

/**
 * Common custom where builders with strict typing
 *
 * Pre-built where builders for common Prisma patterns. These handle
 * complex field types that can't use simple auto-builders.
 */
export const customWhereBuilders = {
    /**
     * For array fields that use Prisma's array operations
     *
     * Handles PostgreSQL array fields using Prisma's hasSome/isEmpty operators.
     * Use this for fields defined as String[] or Int[] in Prisma schema.
     *
     * @param fieldName - The array field name in the database
     * @returns Custom where builder for array operations
     *
     * @example
     * ```typescript
     * commonFields.multiselect({
     *     id: 'categories',
     *     label: 'Categories',
     *     options: categoryOptions,
     *     customWhereBuilder: customWhereBuilders.prismaArrayField<ProductWhere>('categories')
     * });
     *
     * // Generated where clause for 'in' operator:
     * // { categories: { hasSome: ['electronics', 'gadgets'] } }
     * ```
     */
    prismaArrayField: <TWhere = any, T = string>(fieldName: keyof TWhere): InferCustomWhereBuilder<TWhere, T[]> => {
        return (operator, values, _filterId) => {
            const valueArray = Array.isArray(values) ? values : [values];

            return match(operator)
                .with('equals', 'in', () => ({ [fieldName]: { hasSome: valueArray } }) as Partial<TWhere>)
                .with('not', 'notIn', () => ({ NOT: { [fieldName]: { hasSome: valueArray } } as Partial<TWhere> }))
                .with('isNull', () => ({ [fieldName]: { isEmpty: true } }) as Partial<TWhere>)
                .with('isNotNull', () => ({ [fieldName]: { isEmpty: false } }) as Partial<TWhere>)
                .otherwise(() => null);
        };
    },

    /**
     * For case-insensitive text search
     *
     * Provides case-insensitive text matching for all text operations.
     * Essential for user-facing search fields.
     *
     * @param fieldName - The text field name in the database
     * @returns Custom where builder for case-insensitive text operations
     *
     * @example
     * ```typescript
     * commonFields.text({
     *     id: 'email',
     *     label: 'Email',
     *     customWhereBuilder: customWhereBuilders.caseInsensitiveText<UserWhere>('email')
     * });
     *
     * // Generated where clause for 'contains' operator:
     * // { email: { contains: 'gmail', mode: 'insensitive' } }
     * ```
     */
    caseInsensitiveText: <TWhere = any>(fieldName: keyof TWhere): InferCustomWhereBuilder<TWhere, string> => {
        return (operator, value, _filterId) => {
            return match(operator)
                .with('equals', () => ({ [fieldName]: { equals: value, mode: 'insensitive' } }) as Partial<TWhere>)
                .with(
                    'not',
                    () => ({ [fieldName]: { not: { equals: value, mode: 'insensitive' } } }) as Partial<TWhere>,
                )
                .with('contains', () => ({ [fieldName]: { contains: value, mode: 'insensitive' } }) as Partial<TWhere>)
                .with(
                    'startsWith',
                    () => ({ [fieldName]: { startsWith: value, mode: 'insensitive' } }) as Partial<TWhere>,
                )
                .with('endsWith', () => ({ [fieldName]: { endsWith: value, mode: 'insensitive' } }) as Partial<TWhere>)
                .with('isNull', () => ({ [fieldName]: null }) as Partial<TWhere>)
                .with('isNotNull', () => ({ [fieldName]: { not: null } }) as Partial<TWhere>)
                .otherwise(() => ({ [fieldName]: value }) as Partial<TWhere>);
        };
    },

    /**
     * For searchContent pattern matching (used in claims for names)
     *
     * Special builder for fields that store multiple values in a searchContent column
     * using a specific pattern format. Common in legacy schemas.
     *
     * @param pattern - The pattern identifier (e.g., 'claimant', 'injured_party')
     * @returns Custom where builder for pattern-based search
     *
     * @example
     * ```typescript
     * commonFields.text({
     *     id: 'claimant-name',
     *     label: 'Claimant Name',
     *     customWhereBuilder: customWhereBuilders.searchContentField<ClaimWhere>('claimant')
     * });
     *
     * // Searches for pattern: %claimant_start!%John Smith%!claimant_end%
     * ```
     */
    searchContentField: <TWhere = any>(pattern: string): InferCustomWhereBuilder<TWhere, string> => {
        return (operator, value, _filterId) => {
            const searchPattern = `%${pattern}_start!%${value}%!${pattern}_end%`;

            // searchContent is a hardcoded field name (not a generic keyof TWhere),
            // so the intermediate unknown cast is required for these conditions
            return match(operator)
                .with(
                    'equals',
                    'contains',
                    () =>
                        ({
                            searchContent: { contains: searchPattern, mode: 'insensitive' },
                        }) as unknown as Partial<TWhere>,
                )
                .with('not', () => ({
                    NOT: {
                        searchContent: { contains: searchPattern, mode: 'insensitive' },
                    } as unknown as Partial<TWhere>,
                }))
                .with('isNull', () => ({
                    NOT: {
                        searchContent: { contains: `%${pattern}_start!%` },
                    } as unknown as Partial<TWhere>,
                }))
                .with(
                    'isNotNull',
                    () =>
                        ({
                            searchContent: { contains: `%${pattern}_start!%` },
                        }) as unknown as Partial<TWhere>,
                )
                .otherwise(() => null);
        };
    },

    /**
     * For JSONB array fields using Prisma's array_contains operator
     *
     * Designed for Json fields that store arrays (e.g., products, issues, outcomes).
     * Uses Prisma's `array_contains` operator which works with JSONB columns.
     *
     * NOTE: This does NOT work for Prisma scalar arrays (String[], Int[]).
     * For scalar arrays, use a custom where builder with `hasSome` operator instead.
     *
     * @param fieldName - The JSONB array field name in the database
     * @returns Custom where builder using array_contains
     *
     * @example
     * ```typescript
     * // For JSONB fields (Json type in Prisma)
     * commonFields.multiselect({
     *     id: 'issues',
     *     label: 'Issues',
     *     options: issueOptions,
     *     customWhereBuilder: customWhereBuilders.arrayContainsField<ComplaintWhere>('issues')
     * });
     *
     * // Generated where clause:
     * // { OR: [{ issues: { array_contains: ['issue1'] } }, { issues: { array_contains: ['issue2'] } }] }
     * ```
     */
    arrayContainsField: <TWhere = any, T = string>(fieldName: keyof TWhere): InferCustomWhereBuilder<TWhere, T[]> => {
        return (operator, values, _filterId) => {
            const valueArray = Array.isArray(values) ? values : [values];

            return match(operator)
                .with('equals', 'in', () => ({
                    OR: valueArray.map((item) => ({
                        [fieldName]: { array_contains: item },
                    })) as Partial<TWhere>[],
                }))
                .with('not', 'notIn', () => ({
                    NOT: {
                        OR: valueArray.map((item) => ({
                            [fieldName]: { array_contains: item },
                        })) as Partial<TWhere>[],
                    },
                }))
                .with('isNull', () => ({ [fieldName]: { equals: [] } }) as Partial<TWhere>)
                .with('isNotNull', () => ({ [fieldName]: { not: { equals: [] } } }) as Partial<TWhere>)
                .otherwise(() => null);
        };
    },

    /**
     * For relation fields with many-to-many relationships
     *
     * Handles filtering through relation tables (e.g., UserRoles, ProductCategories).
     * Essential for many-to-many relationships in Prisma.
     *
     * @param relationName - The relation field name in Prisma schema
     * @param fieldName - The field to filter on within the relation
     * @returns Custom where builder for relation filtering
     *
     * @example
     * ```typescript
     * commonFields.multiselect({
     *     id: 'roles',
     *     label: 'User Roles',
     *     options: roleOptions,
     *     customWhereBuilder: customWhereBuilders.relationArrayField<UserWhere>('UserRoles', 'roleId')
     * });
     *
     * // Generated where clause:
     * // { UserRoles: { some: { roleId: { in: [1, 2, 3] } } } }
     * ```
     */
    relationArrayField: <TWhere = any>(
        relationName: keyof TWhere,
        fieldName: string,
    ): InferCustomWhereBuilder<TWhere, any[]> => {
        return (operator, values, _filterId) => {
            const valueArray = Array.isArray(values) ? values : [values];

            return match(operator)
                .with(
                    'equals',
                    'in',
                    () =>
                        ({
                            [relationName]: {
                                some: {
                                    [fieldName]: { in: valueArray },
                                },
                            },
                        }) as Partial<TWhere>,
                )
                .with('not', 'notIn', () => ({
                    NOT: {
                        [relationName]: {
                            some: {
                                [fieldName]: { in: valueArray },
                            },
                        },
                    } as Partial<TWhere>,
                }))
                .with('isNull', () => ({
                    NOT: {
                        [relationName]: { some: {} },
                    } as Partial<TWhere>,
                }))
                .with(
                    'isNotNull',
                    () =>
                        ({
                            [relationName]: { some: {} },
                        }) as Partial<TWhere>,
                )
                .otherwise(() => null);
        };
    },

    /**
     * For string fields that need multiselect filtering
     *
     * Handles multiselect filtering on regular string fields by generating OR conditions.
     * Use this when you have a string field but want to allow filtering by multiple values.
     *
     * @param fieldName - The string field name in the database
     * @returns Custom where builder for string multiselect operations
     *
     * @example
     * ```typescript
     * commonFields.multiselect({
     *     id: 'cause-of-loss',
     *     label: 'Cause of loss',
     *     options: causeOptions,
     *     customWhereBuilder: customWhereBuilders.stringMultiselectField<ClaimWhere>('causeOfLoss')
     * });
     *
     * // Generated where clause for 'in' operator with multiple values:
     * // { OR: [{ causeOfLoss: 'fire' }, { causeOfLoss: 'flood' }] }
     *
     * // Generated where clause for 'in' operator with single value:
     * // { causeOfLoss: 'fire' }
     * ```
     */
    stringMultiselectField: <TWhere = any>(fieldName: keyof TWhere): InferCustomWhereBuilder<TWhere, string[]> => {
        return (operator, values, _filterId) => {
            const valueArray = Array.isArray(values) ? values : [values];

            return match(operator)
                .with('equals', 'in', () => {
                    // For single value, use direct equality
                    if (valueArray.length === 1) {
                        return { [fieldName]: valueArray[0] } as Partial<TWhere>;
                    }
                    // For multiple values, use OR
                    return {
                        OR: valueArray.map((v) => ({ [fieldName]: v })) as Partial<TWhere>[],
                    };
                })
                .with('not', 'notIn', () => {
                    // For single value, use direct NOT
                    if (valueArray.length === 1) {
                        return { NOT: { [fieldName]: valueArray[0] } as Partial<TWhere> };
                    }
                    // For multiple values, use NOT + OR
                    return {
                        NOT: {
                            OR: valueArray.map((v) => ({ [fieldName]: v })) as Partial<TWhere>[],
                        },
                    };
                })
                .with('isNull', () => ({ [fieldName]: null }) as Partial<TWhere>)
                .with('isNotNull', () => ({ [fieldName]: { not: null } }) as Partial<TWhere>)
                .otherwise(() => null);
        };
    },
};

/**
 * Build a Prisma where condition that matches a search value against
 * firstName and lastName fields, with multi-word support.
 *
 * For multi-word input like "Jane Smith", splits on whitespace and
 * requires each token to match in at least one of the name fields:
 *   AND: [
 *     { OR: [{ firstName: { contains: "Jane" } }, { lastName: { contains: "Jane" } }] },
 *     { OR: [{ firstName: { contains: "Smith" } }, { lastName: { contains: "Smith" } }] },
 *   ]
 *
 * For single-word input, produces the simpler:
 *   OR: [{ firstName: { contains: "Jane" } }, { lastName: { contains: "Jane" } }]
 */
export function buildNameSearchCondition<TWhere extends { firstName?: any; lastName?: any }>(
    value: string,
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'not' = 'contains',
): PrismaWhereClause<TWhere> | null {
    const tokens = value.trim().split(/\s+/).filter(Boolean);

    if (!tokens.length) {
        return null;
    }

    function buildTokenCondition(
        token: string,
        matchOperator: 'contains' | 'equals' | 'startsWith' | 'endsWith',
    ): PrismaWhereClause<TWhere> {
        return {
            OR: [
                { firstName: { [matchOperator]: token, mode: 'insensitive' } },
                { lastName: { [matchOperator]: token, mode: 'insensitive' } },
            ],
        } as PrismaWhereClause<TWhere>;
    }

    if (operator === 'not') {
        const matchConditions = tokens.map((token) => buildTokenCondition(token, 'equals'));

        return {
            NOT: tokens.length === 1 ? matchConditions[0] : { AND: matchConditions },
        } as PrismaWhereClause<TWhere>;
    }

    if (tokens.length === 1) {
        return buildTokenCondition(tokens[0], operator);
    }

    return {
        AND: tokens.map((token) => buildTokenCondition(token, operator)),
    };
}

/**
 * Type-safe factory for creating common field configurations
 *
 * Main factory function that provides type-safe methods for creating
 * field configurations for all common field types. Automatically handles
 * where clause generation when dbField is provided.
 *
 * @template TWhere - The Prisma where input type for type safety
 * @returns Object with factory methods for each field type
 *
 * @example
 * ```typescript
 * // Create typed field factory
 * const fields = createCommonFields<UserWhere>();
 *
 * // Use factory methods to create fields
 * const nameField = fields.text({
 *     id: 'name',
 *     label: 'User Name',
 *     dbField: 'name' // Auto-generates where clauses
 * });
 *
 * const statusField = fields.select({
 *     id: 'status',
 *     label: 'Status',
 *     options: [{ label: 'Active', value: 'active' }],
 *     dbField: 'status'
 * });
 * ```
 */
export function createCommonFields<TWhere>() {
    return {
        id: (
            options: {
                id: string;
                label?: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, bigint | null, 'bigint'>;
            } & Omit<
                Partial<TypedFieldConfig<bigint | null, TWhere, 'bigint'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<bigint | null, TWhere, 'bigint'> => ({
            type: 'bigint' as const,
            transform: (value: any) => {
                // Handle array for between operator - return as any to bypass type checking
                // The array will be handled by the where builder
                if (Array.isArray(value)) {
                    return value.map((v) => safeBigInt(v)) as any;
                }
                // Handle single value
                return safeBigInt(value);
            },
            format: (value: bigint | null) => (value ? String(value) : ''),
            label: 'ID',
            ...options,
        }),

        text: (
            options: {
                id: string;
                label: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, string, 'text'>;
            } & Omit<
                Partial<TypedFieldConfig<string, TWhere, 'text'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<string, TWhere, 'text'> => ({
            type: 'text',
            defaultOperator: 'contains',
            ...options,
        }),

        textSearch: (
            options: {
                id: string;
                label?: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, string, 'textSearch'>;
            } & Omit<
                Partial<TypedFieldConfig<string, TWhere, 'textSearch'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TextSearchFieldConfig<TWhere> => ({
            type: 'textSearch',
            defaultOperator: 'contains',
            ...options,
        }),

        number: (
            options: {
                id: string;
                label: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, number, 'number'>;
            } & Omit<
                Partial<TypedFieldConfig<number, TWhere, 'number'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<number, TWhere, 'number'> => ({
            type: 'number' as const,
            ...options,
        }),

        date: (
            options: {
                id: string;
                label: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, Date, 'date'>;
            } & Omit<
                Partial<TypedFieldConfig<Date, TWhere, 'date'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<Date, TWhere, 'date'> => ({
            type: 'date' as const,
            transform: (value: any) => {
                // Relative preset token → concrete [start, end] range, resolved against "now".
                if (typeof value === 'string') {
                    const range = resolveRelativeDateRange(value);
                    if (range) return range as any;
                }
                // Handle array for between operator
                if (Array.isArray(value)) {
                    return value.map(toDateOrNull) as any;
                }
                // Handle single value
                return toDateOrNull(value);
            },
            format: (value: any) => formatDateValue(value, true),
            ...options,
        }),

        datetime: (
            options: {
                id: string;
                label: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, Date, 'datetime'>;
            } & Omit<
                Partial<TypedFieldConfig<Date, TWhere, 'datetime'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<Date, TWhere, 'datetime'> => ({
            type: 'datetime' as const,
            transform: (value: any) => {
                // Relative preset token → concrete [start, end] range, resolved against "now".
                if (typeof value === 'string') {
                    const range = resolveRelativeDateRange(value);
                    if (range) return range as any;
                }
                // Handle array for between operator
                if (Array.isArray(value)) {
                    return value.map(toDateOrNull) as any;
                }
                // Handle single value
                return toDateOrNull(value);
            },
            format: (value: any) => formatDateValue(value, false),
            ...options,
        }),

        boolean: (
            options: {
                id: string;
                label: string;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, boolean, 'boolean'>;
            } & Omit<
                Partial<TypedFieldConfig<boolean, TWhere, 'boolean'>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<boolean, TWhere, 'boolean'> => ({
            type: 'boolean' as const,
            ...options,
        }),

        select: <TValue = string>(
            options: {
                id: string;
                label: string;
                options:
                    | Array<{ label: string; value: TValue }>
                    | (() => Promise<Array<{ label: string; value: TValue }>>)
                    | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: TValue }>>);
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, TValue, 'select'>;
            } & Omit<
                Partial<TypedFieldConfig<TValue, TWhere, 'select'>>,
                'customWhereBuilder' | 'id' | 'label' | 'options' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<TValue, TWhere, 'select'> => ({
            type: 'select' as const,
            ...options,
        }),

        multiselect: <
            TValue = string,
            TOp extends OperatorsByFieldType<'multiselect'> = OperatorsByFieldType<'multiselect'>,
        >(
            options: {
                id: string;
                label: string;
                options:
                    | Array<{ label: string; value: TValue }>
                    | (() => Promise<Array<{ label: string; value: TValue }>>)
                    | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: TValue }>>);
                dbField?: keyof TWhere;
                pinned?: boolean;
                operators?: ReadonlyArray<TOp>;
                customWhereBuilder?: (operator: TOp, value: TValue[], fieldName: string) => Partial<TWhere> | null;
            } & Omit<
                Partial<MultiselectFieldConfig<TValue, TWhere>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'dbField' | 'options' | 'multiple' | 'pinned'
            >,
        ): MultiselectFieldConfig<TValue, TWhere> => ({
            type: 'multiselect' as const,
            defaultOperator: 'in',
            ...options,
            options: options.options,
        }),

        user: (
            options: {
                id: string;
                label?: string;
                options?:
                    | Array<{ label: string; value: bigint }>
                    | (() => Promise<Array<{ label: string; value: bigint }>>)
                    | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: bigint }>>);
                dbField?: keyof TWhere;
                pinned?: boolean;
                showUnallocated?: boolean;
                showCurrentUser?: boolean;
                currentUserId?: bigint;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, bigint | bigint[] | null, 'select'>;
            } & Omit<
                Partial<TypedFieldConfig<bigint | bigint[] | null, TWhere, 'select'>>,
                'customWhereBuilder' | 'id' | 'label' | 'options' | 'type' | 'dbField' | 'pinned'
            >,
        ): TypedFieldConfig<bigint | bigint[] | null, TWhere, 'select'> => ({
            type: 'select' as const,
            // Resolve the "Current user" sentinel to the viewer's id at where-build time only.
            transform: (value: unknown) => resolveCurrentUserValue(value, options.currentUserId),
            format: (value: bigint | bigint[] | null) => {
                if (value === null) return '';
                if (Array.isArray(value)) {
                    return value.map((v) => String(v)).join(', ');
                }
                return String(value);
            },
            label: 'User',
            renderValue:
                options.showUnallocated || options.showCurrentUser
                    ? createUserFilterValue(!!options.showUnallocated, !!options.showCurrentUser)
                    : UserFilterValue,
            ...options,
            // Default user options loader if none specified; layer in the "Unallocated" and
            // "Current user" sentinels so the chip can resolve their numeric values to labels.
            options: buildUserOptionsLoader<TWhere>(options.options, {
                showUnallocated: options.showUnallocated,
                includeCurrentUser: options.currentUserId != null,
            }),
            // Use buildUserWhere for automatic where building if dbField is provided
            customWhereBuilder: (options.customWhereBuilder ||
                (options.dbField
                    ? (operator: OperatorsByFieldType<'select'>, value: bigint | bigint[] | null) =>
                          buildUserWhere(options.dbField!, operator, value)
                    : undefined)) as TypedCustomWhereBuilder<TWhere, bigint | bigint[] | null, 'select'>,
        }),

        /**
         * Handling-party multiselect filter with an optional dynamic "Mine" placeholder that
         * expands to the viewer's `handlingPartyIds` at where-build time (so a shared saved view
         * resolves per-person). Defaults the where-builder to the standard
         * `relationArrayField('HandlingParties', 'handlingPartyId')`.
         */
        handlingParty: (
            options: {
                id: string;
                label?: string;
                options:
                    | Array<{ label: string; value: bigint }>
                    | (() => Promise<Array<{ label: string; value: bigint }>>)
                    | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: bigint }>>);
                relationName?: keyof TWhere;
                relationField?: string;
                showMine?: boolean;
                currentUserHandlingPartyIds?: readonly bigint[];
                pinned?: boolean;
                operators?: ReadonlyArray<OperatorsByFieldType<'multiselect'>>;
                customWhereBuilder?: (
                    operator: OperatorsByFieldType<'multiselect'>,
                    value: bigint[],
                    fieldName: string,
                ) => Partial<TWhere> | null;
            } & Omit<
                Partial<MultiselectFieldConfig<bigint, TWhere>>,
                'customWhereBuilder' | 'id' | 'label' | 'type' | 'options' | 'multiple' | 'pinned' | 'transform'
            >,
        ): MultiselectFieldConfig<bigint, TWhere> => ({
            type: 'multiselect' as const,
            defaultOperator: 'in',
            label: 'Handling parties',
            // Expand the "Mine" sentinel to the viewer's handling-party ids at where-build time only.
            transform: (value: unknown) => resolveMineHandlingParties(value, options.currentUserHandlingPartyIds ?? []),
            ...options,
            // Surface the "Mine" picker option (and its chip label) only when enabled.
            options: options.showMine
                ? withPrependedOption<TWhere>(
                      buildMineHandlingPartyOption(options.currentUserHandlingPartyIds),
                      options.options,
                  )
                : options.options,
            customWhereBuilder: (options.customWhereBuilder ??
                customWhereBuilders.relationArrayField<TWhere>(
                    (options.relationName ?? 'HandlingParties') as keyof TWhere,
                    options.relationField ?? 'handlingPartyId',
                )) as MultiselectFieldConfig<bigint, TWhere>['customWhereBuilder'],
        }),

        status: <TValue extends string = string>(
            options: {
                id: string;
                label?: string;
                options: Array<{ label: string; value: TValue }>;
                dbField?: keyof TWhere;
                pinned?: boolean;
                customWhereBuilder?: TypedCustomWhereBuilder<TWhere, TValue | TValue[], 'select'>;
            } & Omit<
                Partial<TypedFieldConfig<TValue | TValue[], TWhere, 'select'>>,
                'customWhereBuilder' | 'id' | 'label' | 'options' | 'type' | 'dbField' | 'pinned' | 'transform'
            >,
        ): TypedFieldConfig<TValue | TValue[], TWhere, 'select'> => ({
            type: 'select' as const,
            label: 'Status',
            transform: (value: unknown): TValue | TValue[] | null => {
                if (value === null || value === undefined) return null;

                const findConfiguredValue = (candidate: unknown): TValue | null =>
                    options.options.find((option) => option.value === String(candidate))?.value ?? null;

                if (Array.isArray(value)) {
                    const configuredValues: TValue[] = [];
                    for (const item of value) {
                        const configuredValue = findConfiguredValue(item);
                        if (configuredValue === null) return null;
                        configuredValues.push(configuredValue);
                    }
                    return configuredValues;
                }

                return findConfiguredValue(value);
            },
            ...options,
        }),

        /**
         * Custom field configuration
         *
         * Allows full control over the field configuration including custom render components.
         * Use this when none of the standard field types meet your requirements.
         *
         * @param config Complete field configuration
         * @returns The configuration as-is with type validation
         *
         * @example
         * ```typescript
         * const customField = fields.custom({
         *     id: 'category',
         *     label: 'Category',
         *     type: 'select',
         *     renderValue: MyCustomComponent,
         *     customWhereBuilder: (operator, value) => {
         *         // Custom where logic
         *     }
         * });
         * ```
         */
        custom: <TValue = any>(config: FieldConfig<TValue, TWhere>): FieldConfig<TValue, TWhere> => config,
    };
}
